import * as React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-solid-svg-icons';

import {
  ACTIVITY_KEY,
  HEARTBEAT_THROTTLE_MS,
  IDLE_LIMIT_MS,
  TICK_MS,
  formatCountdown,
  idleLimitMs,
  idleStateFor,
  msUntilSignOut,
  readLastActivity,
  SIGNED_OUT_KEY,
  warnBeforeMs,
  writeLastActivity,
} from '../../utils/sessionTimeout';
import { signOutForInactivity, signOutAndReload } from '../../utils/signOut';

// Intentional interaction only.
//
// `mousemove` is deliberately NOT here. A cursor crossing the screen is not
// evidence that a practitioner is present — a bumped desk, a trackpad drift or
// a cat on the mouse all produce it, and at a 20s throttle any one of them
// holds a session open forever on an unattended machine. It is also what made
// this untestable: anyone watching the screen for the warning kept resetting
// the very clock they were waiting on, so the modal could never appear.
//
// The rule this restores is consistency. Ambient movement is already ignored
// once the warning is up; it has no business extending the session before it.
// Everything below represents someone deciding to do something, and reading a
// long analysis produces `wheel`/`scroll` continuously anyway.
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];

interface InactivityWatcherProps {
  /**
   * Feed the shared activity clock but never enforce and never render.
   *
   * Used by the PDF popup (App.tsx's `showPDF` branch), which boots the whole
   * app in a second window. Its activity must keep the opener alive, but it
   * must not put a modal over a report the practitioner is printing, and it
   * must not be the window that decides to sign anyone out.
   */
  heartbeatOnly?: boolean;
}

type State = {
  showWarning: boolean;
  remainingMs: number;
};

export class InactivityWatcher extends React.Component<InactivityWatcherProps, State> {
  state: State = {
    showWarning: false,
    remainingMs: 0,
  };

  private lastStampedAt = 0;
  private tickHandle?: number;
  private signingOut = false;
  // Which event last reset the clock. Diagnostic only: if the timer never
  // reaches the warning, this names what keeps waking it up.
  private lastActivitySource = 'mount';

  componentDidMount() {
    // Stamp immediately. Arriving here means the practitioner just signed in
    // or just reloaded, and inheriting a stale stamp from a previous session
    // would sign them straight back out.
    this.stampActivity(true);

    ACTIVITY_EVENTS.forEach(eventName =>
      window.addEventListener(eventName, this.handleActivity, { passive: true } as any),
    );
    window.addEventListener('storage', this.handleStorage);

    if (!this.props.heartbeatOnly) {
      this.tickHandle = window.setInterval(this.tick, TICK_MS);
    }
  }

  componentWillUnmount() {
    ACTIVITY_EVENTS.forEach(eventName =>
      window.removeEventListener(eventName, this.handleActivity),
    );
    window.removeEventListener('storage', this.handleStorage);

    if (this.tickHandle !== undefined) {
      window.clearInterval(this.tickHandle);
      this.tickHandle = undefined;
    }
  }

  private stampActivity(force = false, source = 'mount') {
    const now = Date.now();
    if (!force && now - this.lastStampedAt < HEARTBEAT_THROTTLE_MS) return;
    this.lastStampedAt = now;
    this.lastActivitySource = source;
    writeLastActivity(now);
  }

  private handleActivity = (event: Event) => {
    // While the warning is up, ambient events must NOT dismiss it. A bumped
    // desk, a passing cursor or a mouse the cat is sitting on would otherwise
    // cancel a security timeout on behalf of someone who left the room. The
    // button is the only thing in this window that clears it.
    if (this.state.showWarning) return;
    this.stampActivity(false, event.type);
  };

  private handleStorage = (event: StorageEvent) => {
    if (event.key === SIGNED_OUT_KEY && event.newValue) {
      this.handleRemoteSignOut();
      return;
    }

    if (event.key !== ACTIVITY_KEY || !event.newValue) return;

    // Another window stamped the clock. That stamp only exists because someone
    // was genuinely active there — most often the PDF popup — so it counts as
    // real activity and does clear a warning this window is showing.
    if (this.state.showWarning) {
      this.setState({ showWarning: false, remainingMs: 0 });
    }
  };

  /**
   * Another window signed out, so the tokens backing this one are already
   * gone and nothing on screen here is still authorized.
   *
   * The PDF popup is the case that matters: it holds a fully rendered client
   * report, drawn from its own sessionStorage copy, and would otherwise sit
   * displaying name, values and analysis on an unattended machine long after
   * the app declared the session over. It closes. Any other window reloads,
   * which lands it on the sign-in screen.
   *
   * By construction this cannot interrupt someone mid-print: working in the
   * popup stamps the shared clock, so a session that reached sign-out was one
   * nobody was using.
   */
  private handleRemoteSignOut() {
    if (this.signingOut) return;
    this.signingOut = true;

    if (this.tickHandle !== undefined) {
      window.clearInterval(this.tickHandle);
      this.tickHandle = undefined;
    }

    if (this.props.heartbeatOnly) {
      // Permitted here because the popup was opened by script. If a browser
      // refuses anyway, the reload below still clears the report from view.
      try {
        window.close();
      } catch (err) {
        // Fall through to the reload.
      }
    }

    window.location.reload();
  }

  private tick = () => {
    if (this.signingOut) return;

    const limitMs = idleLimitMs();
    const lastActivity = readLastActivity();
    const now = Date.now();
    const idleState = idleStateFor(lastActivity, now, limitMs);

    // Only when a dev override is active, so normal development stays quiet.
    // Without this there is no way to tell "the timer is running" apart from
    // "the override silently reverted to 60 minutes" except by waiting an hour.
    if (limitMs !== IDLE_LIMIT_MS) {
      const idleSeconds = Math.round((now - (lastActivity || now)) / 1000);
      // A stamp newer than anything this window wrote came from somewhere else
      // — normally the PDF popup. Naming that is the difference between "the
      // popup heartbeat is working" and a confusing `mount` that looks stale.
      const source =
        lastActivity && lastActivity > this.lastStampedAt
          ? 'another window'
          : this.lastActivitySource;
      // eslint-disable-next-line no-console
      console.log(
        `[inactivity] idle ${idleSeconds}s / limit ${Math.round(limitMs / 1000)}s ` +
          `→ ${idleState}, warns at ${Math.round((limitMs - warnBeforeMs(limitMs)) / 1000)}s ` +
          `(last activity: ${source})`,
      );
    }

    if (idleState === 'expired') {
      this.signingOut = true;
      if (this.tickHandle !== undefined) {
        window.clearInterval(this.tickHandle);
        this.tickHandle = undefined;
      }
      signOutForInactivity();
      return;
    }

    if (idleState === 'warning') {
      this.setState({
        showWarning: true,
        remainingMs: msUntilSignOut(lastActivity, now, limitMs),
      });
      return;
    }

    if (this.state.showWarning) {
      this.setState({ showWarning: false, remainingMs: 0 });
    }
  };

  private handleStaySignedIn = () => {
    this.setState({ showWarning: false, remainingMs: 0 }, () => this.stampActivity(true));
  };

  private handleSignOutNow = () => {
    if (this.signingOut) return;
    this.signingOut = true;
    // No reason stamped: this one was deliberate, so the sign-in screen has
    // nothing to explain.
    signOutAndReload();
  };

  render() {
    if (this.props.heartbeatOnly || !this.state.showWarning) {
      return null;
    }

    return (
      <Modal
        show
        onHide={this.handleStaySignedIn}
        backdrop="static"
        keyboard={false}
        data-test="inactivity-warning-modal"
      >
        <Modal.Header style={{ backgroundColor: '#337ab7', color: '#fff' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faClock} /> Still there?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/*
            Kept short on purpose. Whoever this modal reaches has just walked
            back to the desk with a countdown running — it has to be readable
            at a glance, so the lead line carries the only thing that changes
            a decision (unsaved work is at risk) and the rationale comes last.
          */}
          <p style={{ fontSize: '16px' }} data-test="inactivity-countdown">
            Signing out in <strong>{formatCountdown(this.state.remainingMs)}</strong> &mdash;
            anything unsaved will be lost.
          </p>
          <p style={{ marginBottom: 0 }} className="text-muted">
            LabSmarts signs practitioners out after an hour of inactivity to keep client health
            information private on unattended computers.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={this.handleSignOutNow}
            data-test="inactivity-sign-out-now"
          >
            Sign out now
          </Button>
          <Button onClick={this.handleStaySignedIn} data-test="inactivity-stay-signed-in">
            Stay signed in
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
